import BenchlingConfig from 'BenchlingConfig';
import getConfig from 'getConfig';
import {RunQueryOutputLegacyData} from 'insights/warehouse/AnalyticsWarehouseTypes';
import cellToItemIds from 'insights/warehouse/cellToItemIds';
import APIUtil from 'util/APIUtil';
import {JSONValue} from 'util/Types';

/** Supported chip item types - should be a subset of the types BenchChip supports
 *
 * Keep in sync with benchling/insights/analytics_visualizations/chip_items/chip_items.py ChipItemType
 */
export enum ChipItemType {
  BATCH = 'BATCH',
  BIOENTITY = 'BIOENTITY',
  ENTRY = 'ENTRY',
  PROCESS = 'PROCESS',
  PROCESS_OUTPUT = 'PROCESS_OUTPUT',
  PROCESS_TASK = 'PROCESS_TASK',
  REQUEST = 'REQUEST',
  STORABLE = 'STORABLE',
  STUDY = 'STUDY',
  WORKSHEET = 'WORKSHEET',
}

export const CHIP_ITEM_TYPE_TO_PREDICATE: {
  [type in ChipItemType]: (stringValue: string) => boolean;
} = {
  [ChipItemType.BATCH]: (stringValue) => APIUtil.isBatch(stringValue),
  [ChipItemType.BIOENTITY]: (stringValue) => APIUtil.isBiologicalEntity(stringValue),
  [ChipItemType.ENTRY]: (stringValue) => APIUtil.isEntry(stringValue),
  [ChipItemType.PROCESS]: (stringValue) => APIUtil.isProcess(stringValue),
  [ChipItemType.PROCESS_OUTPUT]: (stringValue) => APIUtil.isProcessOutput(stringValue),
  [ChipItemType.PROCESS_TASK]: (stringValue) => APIUtil.isProcessTask(stringValue),
  [ChipItemType.REQUEST]: (stringValue) => APIUtil.isRequest(stringValue),
  [ChipItemType.STORABLE]: (stringValue) => APIUtil.isStorable(stringValue),
  [ChipItemType.STUDY]: (stringValue) => APIUtil.isStudy(stringValue),
  [ChipItemType.WORKSHEET]: (stringValue) => APIUtil.isWorksheet(stringValue),
};

// API identifiers are a series of lowercase letters, an underscore, then a series of letters/digits.
// Keep in sync with benchling/insights/analytics_visualizations/chip_items/chip_items.py API_ID_PATTERN
const API_ID_PATTERN = /^[a-z]+_[a-zA-Z0-9]+$/;

export function isChipItemId(stringValue: string): boolean {
  // Keep in sync with benchling/insights/analytics_visualizations/chip_items/chip_items.py
  // is_chip_item_api_id
  return (
    Boolean(stringValue.match(API_ID_PATTERN)) &&
    Object.values(CHIP_ITEM_TYPE_TO_PREDICATE).some((predicate) => predicate(stringValue))
  );
}

export function cellToChipItemIds(cell: JSONValue): Array<string> | null {
  // Keep in sync with benchling/insights/analytics_visualizations/chip_items/chip_items.py
  // cell_to_chip_item_api_ids
  return cellToItemIds(cell, isChipItemId);
}

export function runQueryOutputToChipRange(runQueryOutput: RunQueryOutputLegacyData): {
  chipRangeEnd: number;
  overLimit: boolean;
} {
  // Keep in sync with benchling/insights/analytics_visualizations/chip_items/chip_items.py
  // run_query_output_to_chip_range

  // Track item ids in a Set so we can track # unique items in those chips to load
  const tableIds = new Set<string>();
  for (const [rowIndex, row] of runQueryOutput.rows.entries()) {
    for (const cell of row) {
      const cellIds = cellToChipItemIds(cell);
      if (cellIds != null) {
        for (const id of cellIds) {
          tableIds.add(id);
        }
        if (tableIds.size > BenchlingConfig.INSIGHTS_MAX_CHIP_NAMES_TO_RESOLVE) {
          return {chipRangeEnd: rowIndex, overLimit: true};
        }
      }
    }
  }
  return {chipRangeEnd: runQueryOutput.rows.length, overLimit: false};
}

export function runQueryOutputToChipItemIds(runQueryOutput: RunQueryOutputLegacyData): Set<string> {
  // Keep in sync with benchling/insights/analytics_visualizations/chip_items/chip_items.py
  // run_query_output_to_chip_item_api_ids . The main difference is that the # items to load:
  // - the back-end loads MAX_CHIPS_TO_EXPORT items
  // - the front-end loads INSIGHTS_MAX_CHIP_NAMES_TO_RESOLVE items
  const {chipRangeEnd} = runQueryOutputToChipRange(runQueryOutput);
  const allIds = runQueryOutput.rows
    .slice(0, chipRangeEnd)
    .flatMap((row) => row.flatMap((cell) => cellToChipItemIds(cell) ?? []));
  return new Set(allIds);
}

export function getWarningMessageForChipItems(runQueryOutput: RunQueryOutputLegacyData): string | null {
  // Keep in sync with benchling/insights/analytics_visualizations/chip_items/chip_items.py
  // get_warning_message_for_chip_items
  const {overLimit} = runQueryOutputToChipRange(runQueryOutput);
  return overLimit
    ? `Chip names will only be used for the first ${getConfig(
        'INSIGHTS_MAX_CHIP_NAMES_TO_RESOLVE'
      )} values in charts, column sorting, and copying as TSV.`
    : null;
}
